import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { isToday, isYesterday, differenceInCalendarDays } from 'date-fns';
import { Icon } from '~/global/components/icon/Icon';
import { useColor } from '~/global/hooks/useColor';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { ChatRecord } from '~/powersync/generated/schema';
import { useChatActions } from '~/chat/hooks/useChatActions';
import { RightSidebar } from '~/layout/components/right-sidebar/RightSidebar';
import { useRightSidebarDrawer } from '~/layout/components/right-sidebar/RightSidebarDrawerContext';
import { ChatListRow } from './ChatListRow';

type Bucket = 'today' | 'yesterday' | 'previous7Days' | 'older';

const BUCKET_ORDER: Bucket[] = ['today', 'yesterday', 'previous7Days', 'older'];

/** Chat row plus the timestamp of its last message (null until backend sync). */
type ChatListItem = ChatRecord & { lastMessageAt: string | null };

const bucketOf = (date: Date): Bucket => {
  if (isToday(date)) return 'today';
  if (isYesterday(date)) return 'yesterday';
  if (differenceInCalendarDays(new Date(), date) < 7) return 'previous7Days';
  return 'older';
};

/** When the conversation last had activity: last message, else when created. */
const activityAt = (chat: ChatListItem): Date =>
  new Date(chat.lastMessageAt ?? chat.createdAt ?? 0);

/**
 * Implements the Pencil "Chat List Sidebar" (A22AmI): header (chats icon +
 * title + new-chat button), a search field, and the conversation list grouped
 * into date sections. Lives inside the 320px right-sidebar panel.
 */
export interface ChatListPanelProps {}

export const ChatListPanel = ({}: ChatListPanelProps) => {
  const { t } = useTranslation('chat');
  const { createNewChat, deleteChat } = useChatActions();
  const mutedColor = useColor('text-muted');
  const [search, setSearch] = useState('');
  // Dismiss the hosting mobile drawer after acting; `undefined` (no-op) on desktop.
  const drawer = useRightSidebarDrawer();

  const handleNewChat = async () => {
    await createNewChat();
    drawer?.close();
  };

  const { data: chats } = useLivePowerSyncQuery<ChatListItem>(
    db.Chat.queries.findAllActive(),
    [],
  );

  const sections = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = (chats ?? [])
      // Only general conversations belong here. Workout/meal chats are scoped to
      // their screens' right rail (locally-created general chats have a null
      // context until backend sync, so treat null as GENERAL).
      .filter((chat) => chat.context !== 'WORKOUT' && chat.context !== 'MEAL')
      .filter((chat) =>
        query ? (chat.name ?? '').toLowerCase().includes(query) : true,
      )
      // Most recent activity (last message, else creation) first.
      .sort((a, b) => activityAt(b).getTime() - activityAt(a).getTime());

    const groups: Record<Bucket, ChatListItem[]> = {
      today: [],
      yesterday: [],
      previous7Days: [],
      older: [],
    };
    for (const chat of list) {
      groups[bucketOf(activityAt(chat))].push(chat);
    }

    return BUCKET_ORDER.filter((bucket) => groups[bucket].length > 0).map(
      (bucket) => ({ bucket, chats: groups[bucket] }),
    );
  }, [chats, search]);

  return (
    <View className="flex-1">
      {/* Header */}
      <RightSidebar.PanelHeader
        icon="MessageSquare"
        title={t('list.title')}
        actions={
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('list.newChat')}
            onPress={handleNewChat}
            className="h-8 w-8 items-center justify-center rounded-lg web:hover:bg-background"
          >
            <Icon name="SquarePen" size={16} className="text-text-primary" />
          </Pressable>
        }
      />

      {/* Search */}
      <View className="px-5 py-3">
        <View className="h-10 flex-row items-center gap-2 rounded-[20px] bg-background px-3.5">
          <Icon name="Search" size={16} className="text-text-muted" />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder={t('list.searchPlaceholder')}
            placeholderTextColor={mutedColor}
            className="flex-1 text-sm font-inter-regular text-text-primary"
          />
        </View>
      </View>

      {/* List */}
      <ScrollView
        className="flex-1"
        contentContainerClassName="px-3 py-2"
        showsVerticalScrollIndicator={false}
      >
        {sections.map(({ bucket, chats: bucketChats }) => (
          <View key={bucket} className="gap-0.5">
            <View className="px-2 pb-1 pt-3">
              <Text className="text-[11px] font-inter-semibold tracking-[0.5px] text-text-muted">
                {t(`list.sections.${bucket}`)}
              </Text>
            </View>
            {bucketChats.map((chat) => (
              <ChatListRow
                key={chat.id}
                chat={chat}
                timestamp={activityAt(chat)}
                onDelete={() => deleteChat(chat.id)}
                onSelected={drawer?.close}
              />
            ))}
          </View>
        ))}
      </ScrollView>
    </View>
  );
};
